// Dialogs: a small accessible modal (native <dialog>), deleting a chat, sharing a chat.

import { h, Component } from 'preact';
import { Icon } from './icons.js';
import { copyText } from './rich.js';
import {
  shareLinks, createShareLink, revokeShareLink, chatPeople, addChatPerson, removeChatPerson,
} from '../api.js';

// <Modal open title onClose>…</Modal> — uses showModal() so focus is trapped and Esc closes.
export class Modal extends Component {
  constructor(p) { super(p); this.el = null; }
  componentDidMount() { this.sync(); }
  componentDidUpdate() { this.sync(); }
  sync() {
    const d = this.el; if (!d) return;
    if (this.props.open && !d.open) d.showModal();
    if (!this.props.open && d.open) d.close();
  }
  render({ title, onClose, children, wide }) {
    return h('dialog', { className: 'modal' + (wide ? ' wide' : ''), ref: (el) => { this.el = el; },
      onClose: () => onClose && onClose(), onCancel: (e) => { e.preventDefault(); onClose && onClose(); },
      onClick: (e) => { if (e.target === this.el) onClose && onClose(); } },
      h('div', { className: 'modal-box' },
        h('div', { className: 'modal-head' }, h('h3', null, title),
          h('button', { type: 'button', className: 'icon-btn', 'aria-label': 'Close', onClick: () => onClose && onClose() }, h(Icon, { name: 'close' }))),
        h('div', { className: 'modal-body' }, children)));
  }
}

// Deleting a chat asks every time what happens to its folder; keeping it is preselected.
export class DeleteChatDialog extends Component {
  constructor(p) { super(p); this.state = { withFiles: false, busy: false, error: '' }; }
  componentDidUpdate(prev) { if (prev.project !== this.props.project) this.setState({ withFiles: false, busy: false, error: '' }); }
  async go() {
    this.setState({ busy: true, error: '' });
    try { await this.props.onDelete(this.props.project, this.state.withFiles); }
    catch (e) { this.setState({ busy: false, error: e.message }); }
  }
  render({ project, onClose }, { withFiles, busy, error }) {
    if (!project) return h(Modal, { open: false, title: '', onClose });
    const folder = `projects/${project.slug}`;
    return h(Modal, { open: true, title: 'Delete this chat?', onClose },
      h('p', { className: 'muted-p' }, `“${project.title}” and its history will be removed. This can’t be undone. Archiving keeps it instead.`),
      h('label', { className: 'radio' + (!withFiles ? ' on' : '') },
        h('input', { type: 'radio', name: 'files', checked: !withFiles, onChange: () => this.setState({ withFiles: false }) }),
        h('span', null, h('b', null, 'Keep its files'), h('small', null, `The folder ${folder} stays in your workspace.`))),
      h('label', { className: 'radio danger' + (withFiles ? ' on' : '') },
        h('input', { type: 'radio', name: 'files', checked: withFiles, onChange: () => this.setState({ withFiles: true }) }),
        h('span', null, h('b', null, 'Also delete the folder'), h('small', null, h('code', null, folder), ' and everything in it is erased from the workspace.'))),
      error ? h('p', { className: 'note err' }, error) : null,
      h('div', { className: 'modal-actions' },
        h('button', { type: 'button', className: 'btn ghost', onClick: onClose }, 'Cancel'),
        h('button', { type: 'button', className: 'btn danger', disabled: busy, onClick: () => this.go() },
          busy ? 'Deleting…' : withFiles ? 'Delete chat and folder' : 'Delete chat')));
  }
}

// Share one chat: a public snapshot link, or named people who can read it live.
export class ShareDialog extends Component {
  constructor(p) {
    super(p);
    this.state = { tab: 'link', links: null, people: null, email: '', busy: false, error: '', copied: false };
  }
  componentDidMount() { if (this.props.project) this.load(); }
  componentDidUpdate(prev) {
    if (prev.project !== this.props.project && this.props.project) {
      // every opening starts on the first tab with fresh data
      this.setState({ tab: 'link', links: null, people: null, email: '', error: '', copied: false });
      this.load();
    }
  }
  async load() {
    const id = this.props.project.id;
    try {
      const [links, people] = await Promise.all([shareLinks(id), chatPeople(id)]);
      this.setState({ links, people, error: '' });
    } catch (e) { this.setState({ error: e.message }); }
  }
  async makeLink() {
    this.setState({ busy: true, error: '' });
    try { const l = await createShareLink(this.props.project.id); this.setState({ links: [l], busy: false }); }
    catch (e) { this.setState({ busy: false, error: e.message }); }
  }
  async revoke(token) {
    try { await revokeShareLink(this.props.project.id, token); this.setState({ links: [] }); }
    catch (e) { this.setState({ error: e.message }); }
  }
  async invite(e) {
    e.preventDefault();
    const email = this.state.email.trim();
    if (!email) return;
    this.setState({ busy: true, error: '' });
    try { const people = await addChatPerson(this.props.project.id, email); this.setState({ people, email: '', busy: false }); }
    catch (err) { this.setState({ busy: false, error: err.message }); }
  }
  async remove(aid) {
    try { this.setState({ people: await removeChatPerson(this.props.project.id, aid) }); }
    catch (e) { this.setState({ error: e.message }); }
  }
  render({ project, onClose }, s) {
    if (!project) return h(Modal, { open: false, title: '', onClose });
    const link = (s.links || [])[0];
    return h(Modal, { open: true, title: `Share “${project.title}”`, onClose },
      h('div', { className: 'tabs seg', role: 'tablist' },
        h('button', { type: 'button', role: 'tab', 'aria-selected': s.tab === 'link', className: 'tab' + (s.tab === 'link' ? ' on' : ''), onClick: () => this.setState({ tab: 'link' }) }, 'Public link'),
        h('button', { type: 'button', role: 'tab', 'aria-selected': s.tab === 'people', className: 'tab' + (s.tab === 'people' ? ' on' : ''), onClick: () => this.setState({ tab: 'people' }) }, 'People')),
      s.tab === 'link' ? h('div', { className: 'share-pane' },
        h('p', { className: 'muted-p' }, 'Anyone with the link can read a snapshot of this chat, including the images and files it shows. No sign-in needed. Reasoning and command output are left out.'),
        link ? h('div', null,
          h('div', { className: 'copy-row' },
            h('input', { readOnly: true, value: link.url, onFocus: (e) => e.target.select(), 'aria-label': 'Share link' }),
            h('button', { type: 'button', className: 'btn primary', onClick: async () => { await copyText(link.url); this.setState({ copied: true }); setTimeout(() => this.setState({ copied: false }), 1500); } },
              h(Icon, { name: 'copy' }), s.copied ? ' Copied' : ' Copy')),
          h('p', { className: 'muted small' }, `Snapshot from ${new Date(link.created_at + 'Z').toLocaleString()}` + (link.files ? ` · ${link.files} file${link.files === 1 ? '' : 's'}` : '')),
          h('div', { className: 'modal-actions left' },
            h('button', { type: 'button', className: 'btn', disabled: s.busy, onClick: () => this.makeLink() }, s.busy ? 'Updating…' : 'Update snapshot'),
            h('button', { type: 'button', className: 'btn ghost danger-text', onClick: () => this.revoke(link.token) }, 'Stop sharing')))
          : h('button', { type: 'button', className: 'btn primary', disabled: s.busy || s.links === null, onClick: () => this.makeLink() },
            h(Icon, { name: 'external' }), s.busy ? ' Creating…' : ' Create public link'))
      : h('div', { className: 'share-pane' },
        h('p', { className: 'muted-p' }, 'People you add can read this chat, as it continues, after signing in with that email address. They can’t write in it or see the rest of the workspace.'),
        h('form', { className: 'copy-row', onSubmit: (e) => this.invite(e) },
          h('input', { type: 'email', placeholder: 'name@example.com', value: s.email, 'aria-label': 'Email address', onInput: (e) => this.setState({ email: e.target.value }) }),
          h('button', { type: 'submit', className: 'btn primary', disabled: s.busy }, s.busy ? 'Adding…' : 'Add')),
        h('ul', { className: 'people' }, (s.people || []).map((p) => h('li', { key: p.id },
          h('span', { className: 'avatar', 'aria-hidden': 'true' }, p.email.slice(0, 1).toUpperCase()),
          h('span', { className: 'grow' }, p.email, h('small', null, p.joined ? 'Can read' : 'Invited')),
          h('button', { type: 'button', className: 'icon-btn', 'aria-label': `Remove ${p.email}`, onClick: () => this.remove(p.id) }, h(Icon, { name: 'close' })))),
          s.people && !s.people.length ? h('li', { className: 'muted' }, 'Not shared with anyone yet.') : null)),
      s.error ? h('p', { className: 'note err' }, s.error) : null);
  }
}
